import React, { useEffect, useState, useCallback } from "react";
import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Subscription } from "rxjs";
import { DatasetService } from "../../../services/DatasetService";
import { TestSetService } from "../../../services/TestSetService";
import { SemanticService } from "../../../services/SemanticService";
import { usePermissions } from "../../../PermissionsProvider";
import moment from "moment";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { TestSet } from "../../../model_gen/evaluation";
import RequestViewModal from "../../Request/RequestViewModal";
import DatasetModification from "./DatasetModification";
import { MultiLayerSemanticChart } from "../../shared/MultiLayerSemanticChart";
import AIReview from "./AIReview";
import ReviewsTable from "../../shared/ReviewsTable";
import AugmentationsTable from "../../shared/AugmentationsTable";
import VersionsTable from "../../shared/VersionsTable";
import DatasetRequestsTable from "./DatasetRequestsTable";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";
import { Card, CardContent } from "../../ui/card";
import { Tooltip } from "../../ui/tooltip";
import OverflowTabs, { TabItem } from "../../ui/overflow-tabs";
import { cn } from "../../../lib/utils";
import {
  BinocularsIcon,
  EditIcon,
  WrenchIcon,
  FileTextIcon,
  CalendarIcon,
  AppWindowIcon,
  FileSearchIcon,
  FlaskConicalIcon,
  GitBranchIcon,
  Loader2Icon,
  RefreshCwIcon,
  TagIcon,
} from "lucide-react";
import DatasetTags from "./DatasetTags";

interface AIReviewPayload {
  review_type: "ai" | "human" | "mixed";
  instructions?: string;
  confidence_threshold?: number;
}

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

enum DatasetS3ImportStatus {
  ImportPending = "IMPORT_PENDING",
  Importing = "IMPORTING",
  Cancelled = "CANCELLED",
  Error = "ERROR",
}

const S3_IMPORT_STATUS_LABEL: Record<DatasetS3ImportStatus, string> = {
  [DatasetS3ImportStatus.ImportPending]: "Import Pending",
  [DatasetS3ImportStatus.Importing]: "Importing",
  [DatasetS3ImportStatus.Cancelled]: "Cancelled",
  [DatasetS3ImportStatus.Error]: "Error",
};

// The semantic distribution chart can be expensive to compute on very large datasets.
// Avoid auto-fetching by default above this threshold; the user can still opt-in.
const SEMANTIC_CHART_AUTOLOAD_MAX_REQUESTS = 200_000;

const getS3ImportStatusBadgeClassName = (
  status: DatasetS3ImportStatus
): string => {
  switch (status) {
    case DatasetS3ImportStatus.Importing:
      return "border-maitai-green/30 text-maitai-green bg-maitai-green/10";
    case DatasetS3ImportStatus.Cancelled:
      return "border-muted-foreground/30 text-muted-foreground bg-muted/20";
    case DatasetS3ImportStatus.Error:
      return "border-red-500/30 text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400";
    case DatasetS3ImportStatus.ImportPending:
    default:
      return "border-blue-500/30 text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-400";
  }
};

const isDatasetS3ImportStatus = (
  status: string | undefined
): status is DatasetS3ImportStatus => {
  return (
    status === DatasetS3ImportStatus.ImportPending ||
    status === DatasetS3ImportStatus.Importing ||
    status === DatasetS3ImportStatus.Cancelled ||
    status === DatasetS3ImportStatus.Error
  );
};

const DatasetDetail: React.FC = () => {
  const { id } = useParams();
  const { apiKey } = usePermissions();
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [sourceTestSets, setSourceTestSets] = useState<TestSet[]>([]);
  const [isSourceTestSetsLoading, setIsSourceTestSetsLoading] =
    useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isCancellingImport, setIsCancellingImport] = useState<boolean>(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);
  const [isReimporting, setIsReimporting] = useState<boolean>(false);
  const [requests, setRequests] = useState<ChatCompletionRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestPageSize] = useState<number>(10);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, ChatCompletionRequest[]>
  >({});
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [isModificationModalVisible, setIsModificationModalVisible] =
    useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<ChatCompletionRequest | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [isAiReviewModalVisible, setIsAiReviewModalVisible] = useState(false);
  const [isAiReviewLoading, setIsAiReviewLoading] = useState(false);
  const [reviewStatus, setReviewStatus] = useState<string | null>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [isReviewsLoading, setIsReviewsLoading] = useState<boolean>(false);
  const [reviewsCurrentPage, setReviewsCurrentPage] = useState<number>(1);
  const [reviewsPageSize, setReviewsPageSize] = useState<number>(10);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [reviewsByPage, setReviewsByPage] = useState<Record<number, any[]>>({});
  const [syncingReviewIds, setSyncingReviewIds] = useState<Set<number>>(
    new Set()
  );
  const [augmentations, setAugmentations] = useState<any[]>([]);
  const [isAugmentationsLoading, setIsAugmentationsLoading] =
    useState<boolean>(false);
  const [augmentationsCurrentPage, setAugmentationsCurrentPage] =
    useState<number>(1);
  const [augmentationsPageSize, setAugmentationsPageSize] =
    useState<number>(10);
  const [totalAugmentations, setTotalAugmentations] = useState<number>(0);
  const [reviewProgress, setReviewProgress] = useState<Record<string, any>>({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [versions, setVersions] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(false);
  const activeReviewStatuses = [
    "CREATED",
    "SCHEDULED",
    "PROCESSING",
    "PENDING",
    "WAITING_HUMAN",
  ];
  const activeReview = reviews.find((review) =>
    activeReviewStatuses.includes(review.status)
  );
  const activeReviewStatus = reviewStatus || activeReview?.status;
  const datasetReviewStatus = String(reviewStatus || "").toUpperCase();
  const hasActiveReview = activeReviewStatuses.includes(datasetReviewStatus);
  const humanReviewStatuses = [
    "CREATED",
    "SCHEDULED",
    "PENDING",
    "WAITING_HUMAN",
  ];
  const hasHumanReviewInProgress = reviews.some(
    (review) =>
      humanReviewStatuses.includes(review.status) &&
      ["human", "mixed"].includes(String(review.review_type).toLowerCase())
  );
  const humanReviewStatus = reviews.find(
    (review) =>
      humanReviewStatuses.includes(review.status) &&
      ["human", "mixed"].includes(String(review.review_type).toLowerCase())
  )?.status;
  const [isSemanticChartEnabled, setIsSemanticChartEnabled] =
    useState<boolean>(false);
  const [semanticChartStatus, setSemanticChartStatus] = useState<
    "checking" | "missing" | "pending" | "none" | "available"
  >("checking");

  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  // Fetch dataset details
  useEffect(() => {
    if (!id) return;

    const subscription = new Subscription();
    let didTimeout = false;
    setIsLoading(true);
    setLoadError(null);

    // Hard timeout so we don't get stuck on the skeleton indefinitely.
    const timeoutId = setTimeout(() => {
      didTimeout = true;
      setLoadError("Dataset request timed out. Please try again.");
      setIsLoading(false);
      // Stop listening for late emissions; otherwise a late response can overwrite state.
      subscription.unsubscribe();
    }, 30000);

    const sub = DatasetService.getDataset(
      Number(id),
      undefined,
      apiKey
    ).subscribe(
      (data: FinetuneDataset) => {
        if (didTimeout) return;
        clearTimeout(timeoutId);
        if (data) {
          setDataset(data);
          setLoadError(null);
        } else {
          setLoadError("Received empty dataset data");
        }
        setIsLoading(false);
      },
      (error: any) => {
        if (didTimeout) return;
        clearTimeout(timeoutId);
        console.error("Error fetching dataset:", error);
        setLoadError(error?.message || "Error fetching dataset");
        setIsLoading(false);
      }
    );

    subscription.add(sub);
    return () => {
      didTimeout = true;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [id, apiKey]);

  useEffect(() => {
    const datasetId = dataset?.id;
    if (!datasetId) {
      setSourceTestSets([]);
      return;
    }

    const subscription = TestSetService.getTestSetsCreatedFromDataset(
      datasetId,
      setIsSourceTestSetsLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        const items = Array.isArray(response) ? response : [];
        setSourceTestSets(items);
      },
      (error: any) => {
        console.error("Error fetching test sets created from dataset:", error);
        setSourceTestSets([]);
      }
    );

    return () => subscription.unsubscribe();
  }, [dataset, apiKey]);

  useEffect(() => {
    if (!id) return;
    setReviewStatus(null);
    const subscription = DatasetService.getDatasetReviewStatus(
      Number(id),
      null,
      apiKey
    ).subscribe(
      (response: any) => {
        setReviewStatus(response?.status || null);
      },
      (error: any) => {
        console.error("Error fetching dataset review status:", error);
        setReviewStatus(null);
      }
    );
    return () => subscription.unsubscribe();
  }, [id, apiKey]);
  // Default semantic chart behavior (reset when switching datasets)
  useEffect(() => {
    if (!dataset) return;
    const requestCount = dataset.request_count || 0;
    const autoEnable = requestCount <= SEMANTIC_CHART_AUTOLOAD_MAX_REQUESTS;
    setIsSemanticChartEnabled(autoEnable);

    // Hide by default for large datasets until we confirm there's something to show.
    if (requestCount <= 0) {
      setSemanticChartStatus("none");
    } else if (autoEnable) {
      setSemanticChartStatus("available");
    } else {
      setSemanticChartStatus("checking");
    }
  }, [dataset?.id]);

  // For very large datasets, check if a semantic distribution exists (without downloading the chart).
  useEffect(() => {
    if (!dataset || !id) return;

    const requestCount = dataset.request_count || 0;
    if (requestCount <= 0) return;
    if (requestCount <= SEMANTIC_CHART_AUTOLOAD_MAX_REQUESTS) return;

    const subscription = new Subscription();

    const payload = {
      chart_details: [
        {
          entity_type: "dataset",
          entity_id: Number(id),
        },
      ],
    };

    const sub = SemanticService.getSemanticComboChartStatus(
      payload,
      null,
      apiKey
    ).subscribe(
      (resp: any) => {
        const status = resp?.status as string | undefined;
        if (status === "none") setSemanticChartStatus("none");
        else if (status === "pending") setSemanticChartStatus("pending");
        else if (status === "missing") setSemanticChartStatus("missing");
        else setSemanticChartStatus("available");
      },
      (error: any) => {
        console.error("Error checking semantic chart status:", error);
        setSemanticChartStatus("missing");
      }
    );

    subscription.add(sub);
    return () => subscription.unsubscribe();
  }, [dataset?.id, id, apiKey]);

  // Poll dataset status while S3 import is running
  useEffect(() => {
    if (!id || !apiKey) return;

    const status = (dataset as any)?.meta?.status as string | undefined;
    if (
      status !== DatasetS3ImportStatus.ImportPending &&
      status !== DatasetS3ImportStatus.Importing
    )
      return;

    const subscription = new Subscription();

    const intervalId = setInterval(() => {
      const sub = DatasetService.getDataset(
        Number(id),
        undefined,
        apiKey
      ).subscribe(
        (data: FinetuneDataset) => {
          if (data) setDataset(data);
        },
        (error: any) => {
          console.error("Error polling dataset status:", error);
        }
      );
      subscription.add(sub);
    }, 10000);

    return () => {
      clearInterval(intervalId);
      subscription.unsubscribe();
    };
  }, [id, apiKey, (dataset as any)?.meta?.status]);

  // Fetch dataset requests
  useEffect(() => {
    const status = (dataset as any)?.meta?.status as string | undefined;
    const isImportInProgress =
      status === DatasetS3ImportStatus.ImportPending ||
      status === DatasetS3ImportStatus.Importing;
    if (isImportInProgress) {
      // Clear cached state to prevent showing stale requests from a previous dataset
      // Only clear if not already empty to avoid render loop (requestsByPage is a dependency)
      if (requests.length > 0) setRequests([]);
      if (Object.keys(requestsByPage).length > 0) setRequestsByPage({});
      if (totalRequests !== 0) setTotalRequests(0);
      return;
    }

    if (id && activeTab === "requests" && !requestsByPage[currentPage]) {
      fetchRequests(currentPage);
    } else if (activeTab === "requests" && requestsByPage[currentPage]) {
      setRequests(requestsByPage[currentPage]);
    }
  }, [
    id,
    currentPage,
    requestPageSize,
    requestsByPage,
    activeTab,
    (dataset as any)?.meta?.status,
  ]);

  const fetchRequests = (page: number) => {
    const status = (dataset as any)?.meta?.status as string | undefined;
    const isImportInProgress =
      status === DatasetS3ImportStatus.ImportPending ||
      status === DatasetS3ImportStatus.Importing;
    if (isImportInProgress) return;

    const offset = (page - 1) * requestPageSize;
    setIsRequestsLoading(true);

    DatasetService.getDatasetRequests(
      Number(id),
      offset,
      requestPageSize,
      setIsRequestsLoading,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setRequests(response.items);
        setTotalRequests(response.total);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching dataset requests:", error);
        setIsRequestsLoading(false);
      }
    );
  };

  // Fetch dataset reviews
  useEffect(() => {
    if (id && activeTab === "reviews" && !reviewsByPage[reviewsCurrentPage]) {
      fetchReviews(reviewsCurrentPage);
    } else if (activeTab === "reviews" && reviewsByPage[reviewsCurrentPage]) {
      setReviews(reviewsByPage[reviewsCurrentPage]);
    }
  }, [id, apiKey, reviewsCurrentPage, reviewsPageSize, activeTab]);

  useEffect(() => {
    if (id && !reviewsByPage[1]) {
      fetchReviews(1);
    }
  }, [id, apiKey, reviewsByPage]);

  const fetchReviews = (page: number) => {
    if (!id) return;

    setIsReviewsLoading(true);
    const offset = (page - 1) * reviewsPageSize;

    DatasetService.getDatasetReviews(
      Number(id),
      offset,
      reviewsPageSize,
      setIsReviewsLoading,
      apiKey
    ).subscribe(
      (response: { items: any[]; total: number }) => {
        setReviews(response.items || []);
        setTotalReviews(response.total);
        setReviewsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
      },
      (error: any) => {
        console.error("Error fetching dataset reviews:", error);
      }
    );
  };

  const handleStartAiReview = (payload: AIReviewPayload) => {
    if (!id) return;

    const reviewPayload = {
      review_type: payload.review_type,
      ...(payload.review_type !== "ai" && {
        instructions: payload.instructions || "",
      }),
      ...(payload.review_type === "mixed" && {
        confidence_threshold: payload.confidence_threshold,
      }),
    };

    setIsAiReviewLoading(true);
    DatasetService.startAiReview(
      Number(id),
      setIsAiReviewLoading,
      apiKey,
      reviewPayload
    ).subscribe(
      (response: any) => {
        console.log("AI review started successfully", response);
        setIsAiReviewModalVisible(false);

        if (dataset) {
          setDataset({
            ...dataset,
            meta: {
              ...dataset.meta,
              review_status: response?.status || "CREATED",
            },
          });
        }
        setReviewStatus(response?.status || "CREATED");
        setReviewsByPage({});
        setReviewsCurrentPage(1);
        fetchReviews(1);
      },
      (error: any) => {
        console.error("Error starting AI review:", error);
      }
    );
  };

  const handleRetryReview = (reviewId: number) => {
    if (window.confirm("Are you sure you want to retry this review?")) {
      DatasetService.retryDatasetReview(reviewId, null, apiKey).subscribe(
        () => {
          console.log("Review retry started");
          // Clear the reviews page cache to force a refresh
          setReviewsByPage({});
          // Reset to first page and fetch fresh data
          setReviewsCurrentPage(1);
          fetchReviews(1);
        },
        (error: any) => {
          console.error("Error retrying review:", error);
        }
      );
    }
  };

  const handleForceProlificSync = (reviewId: number) => {
    setSyncingReviewIds((prev) => {
      const next = new Set(prev);
      next.add(reviewId);
      return next;
    });
    DatasetService.forceProlificReviewSync(reviewId, null, apiKey).subscribe(
      () => {
        setReviewsByPage({});
        setReviewsCurrentPage(1);
        fetchReviews(1);
        setSyncingReviewIds((prev) => {
          const next = new Set(prev);
          next.delete(reviewId);
          return next;
        });
      },
      (error: any) => {
        console.error("Error forcing Prolific sync:", error);
        setSyncingReviewIds((prev) => {
          const next = new Set(prev);
          next.delete(reviewId);
          return next;
        });
      }
    );
  };

  useEffect(() => {
    const subscription = new Subscription();

    const fetchProgress = () => {
      if (!id) return;

      const sub = DatasetService.getDatasetReviewProgressByDataset(
        Number(id),
        null,
        apiKey
      ).subscribe(
        (progressData: Record<string, any>) => {
          setReviewProgress(progressData);
        },
        (error: any) => {
          console.error(error);
        }
      );
      subscription.add(sub);
    };

    // Always fetch progress once when component mounts
    fetchProgress();

    // Check if any reviews need monitoring
    const hasIncompleteReviews = reviews.some(
      (review) => review.status !== "COMPLETED" && review.status !== "ERROR"
    );

    // Set up interval only if there are incomplete reviews
    let intervalId: NodeJS.Timeout | undefined;
    if (hasIncompleteReviews) {
      intervalId = setInterval(fetchProgress, 5000);
    }

    // Cleanup on unmount
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      subscription.unsubscribe();
    };
  }, [id, apiKey, reviews]);

  // Add useEffect for fetching augmentations
  const fetchAugmentations = useCallback(
    (page: number) => {
      if (!id) return;
      const offset = (page - 1) * augmentationsPageSize;
      setIsAugmentationsLoading(true);

      DatasetService.getDatasetAugmentations(
        Number(id),
        offset,
        augmentationsPageSize,
        setIsAugmentationsLoading,
        apiKey
      ).subscribe(
        (response: { items: any[]; total: number }) => {
          setAugmentations(response.items || []);
          setTotalAugmentations(response.total);
          setIsAugmentationsLoading(false);
        },
        (error: any) => {
          console.error("Error fetching augmentations:", error);
          setIsAugmentationsLoading(false);
        }
      );
    },
    [id, augmentationsPageSize, apiKey]
  );

  useEffect(() => {
    if (id && activeTab === "augmentations") {
      fetchAugmentations(augmentationsCurrentPage);
    }
  }, [id, augmentationsCurrentPage, activeTab, fetchAugmentations]);

  // Add useEffect for polling augmentation status
  useEffect(() => {
    // Only poll if we're on the augmentations tab
    if (activeTab !== "augmentations") return;

    // Find any augmentations that are still running
    const runningAugmentations = augmentations.filter(
      (aug) => aug.status === "RUNNING" || aug.status === "CREATED"
    );

    // If no running augmentations, don't poll
    if (runningAugmentations.length === 0) return;

    const subscription = new Subscription();

    const pollInterval = setInterval(() => {
      // For each running augmentation, check its status
      runningAugmentations.forEach((aug) => {
        const sub = DatasetService.getAugmentationDetails(
          aug.augmentation_id,
          null,
          apiKey
        ).subscribe(
          (details: any) => {
            // If status changed to completed or error, refresh the table
            if (details.status === "COMPLETED" || details.status === "ERROR") {
              setAugmentationsCurrentPage(1);
              fetchAugmentations(1);
            }
          },
          (error: any) => {
            console.error("Error checking augmentation status:", error);
          }
        );
        subscription.add(sub);
      });
    }, 5000);

    return () => {
      clearInterval(pollInterval);
      subscription.unsubscribe();
    };
  }, [activeTab, augmentations, apiKey, fetchAugmentations]);

  // Update useEffect to set initial tab from URL
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Fetch versions when the versions tab is activated
  useEffect(() => {
    if (id && activeTab === "versions" && versions.length === 0) {
      setVersionsLoading(true);
      DatasetService.getDatasetVersions(
        Number(id),
        setVersionsLoading,
        apiKey
      ).subscribe(
        (data: any[]) => {
          if (data) {
            setVersions(data);
          } else {
            console.error("Received empty versions data");
          }
        },
        (error: any) => {
          console.error("Error fetching versions:", error);
        }
      );
    }
  }, [id, apiKey, activeTab, versions.length]);

  const renderOverviewTab = () => (
    <div className="space-y-8">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">
                  Total Requests
                </p>
              </div>
              <p className="text-3xl font-gc-demi-bold text-foreground">
                {dataset?.request_count || 0}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">
                  Date Created
                </p>
              </div>
              <p className="text-lg font-gc-med text-foreground">
                {dataset?.date_created
                  ? moment(dataset.date_created).format("MMM D, YYYY")
                  : "-"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Section */}
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-lg font-gc-med text-foreground mb-4">Details</h3>
          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Description
              </p>
              <p className="text-base font-gc-reg text-foreground whitespace-pre-wrap">
                {dataset?.description || "No description provided"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                Source
              </p>
              <div className="text-base font-gc-reg text-foreground">
                {dataset?.intent_group_name &&
                !dataset?.application_id &&
                !dataset?.intent_id ? (
                  <Link
                    to={`/intent-groups/${dataset?.intent_group_id}`}
                    className="text-maitai-green hover:underline">
                    {dataset?.intent_group_name}
                  </Link>
                ) : dataset?.application_name &&
                  dataset?.intent_name &&
                  !dataset?.intent_group_id ? (
                  <>
                    <Link
                      to={`/application/${dataset?.application_id}`}
                      className="text-maitai-green hover:underline">
                      {dataset?.application_name}
                    </Link>
                    {" / "}
                    <Link
                      to={`/application/${dataset?.application_id}/intent/${dataset?.intent_id}`}
                      className="text-maitai-green hover:underline">
                      {dataset?.intent_name}
                    </Link>
                  </>
                ) : (
                  "Unknown source"
                )}
              </div>
            </div>

            {(() => {
              const meta: any = (dataset as any)?.meta || {};
              const usedInTest = meta.used_in_test === true;

              if (!usedInTest && sourceTestSets.length === 0) return null;

              return (
                <div>
                  <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                    Used in Test Sets
                  </p>

                  {isSourceTestSetsLoading ? (
                    <p className="text-base font-gc-reg text-foreground">
                      Loading test sets…
                    </p>
                  ) : sourceTestSets.length > 0 ? (
                    <ul className="space-y-1">
                      {sourceTestSets.map((testSet) => (
                        <li key={testSet.id}>
                          <Link
                            to={`/intent-group/${testSet.intent_group_id}/testsets/${testSet.id}`}
                            className="text-maitai-green hover:underline">
                            {testSet.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-base font-gc-reg text-foreground">
                      No linked test sets found.
                    </p>
                  )}
                </div>
              );
            })()}

            {(() => {
              const meta: any = dataset?.meta || {};
              const datasourceType =
                meta.datasource_type ||
                meta.data_source_type ||
                meta.import_source;
              const importStatus = String(meta.status || "");
              const s3Url: string | undefined =
                meta.s3_url || meta.import_s3_url || meta.s3_path;
              const s3FileGlob: string | undefined =
                meta.s3_file_glob || meta.import_file_glob;
              const s3Region: string | undefined = meta.s3_region;

              if (datasourceType !== "s3" || !s3Url) return null;

              return (
                <div>
                  <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                    S3 Source
                  </p>
                  <p className="text-base font-gc-reg text-foreground break-all">
                    {s3Url}
                  </p>
                  {(s3FileGlob || s3Region) && (
                    <p className="text-sm text-muted-foreground font-gc-reg mt-2">
                      {s3FileGlob ? `File glob: ${s3FileGlob}` : ""}
                      {s3FileGlob && s3Region ? " • " : ""}
                      {s3Region ? `Region: ${s3Region}` : ""}
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Semantic Distribution Chart */}
      {dataset &&
        (dataset.request_count || 0) > 0 &&
        (isSemanticChartEnabled ? (
          <MultiLayerSemanticChart
            chartData={[
              {
                entityType: "dataset",
                entityTypeId: id || "",
                name: dataset.name,
              },
            ]}
            apiKey={apiKey}
          />
        ) : semanticChartStatus === "checking" ||
          semanticChartStatus === "none" ? null : (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between gap-6">
                <div className="space-y-1">
                  <h3 className="text-lg font-gc-med text-foreground">
                    Semantic Distribution
                  </h3>
                  <p className="text-sm text-muted-foreground font-gc-reg">
                    {semanticChartStatus === "pending"
                      ? "Chart is being generated. You can open it once ready."
                      : "This chart can take a while to generate for very large datasets. Load it when you’re ready."}
                  </p>
                </div>
                <Button
                  onClick={() => setIsSemanticChartEnabled(true)}
                  className="gap-2">
                  Load Chart
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
    </div>
  );

  const renderRequestsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Requests</h3>
          <p className="text-sm text-muted-foreground">
            View and manage dataset requests
          </p>
        </div>
        <Button
          onClick={() => setIsModificationModalVisible(true)}
          className="gap-2">
          <WrenchIcon className="w-4 h-4" />
          Broad Modification
        </Button>
      </div>

      <DatasetRequestsTable
        requests={requests}
        requestsTotal={totalRequests}
        currentPage={currentPage}
        pageSize={requestPageSize}
        loading={isRequestsLoading}
        onPageChange={setCurrentPage}
        onRowClick={handleViewRequest}
        apiKey={apiKey}
        emptyTitle={
          (dataset as any)?.meta?.status ===
            DatasetS3ImportStatus.ImportPending ||
          (dataset as any)?.meta?.status === DatasetS3ImportStatus.Importing
            ? "Import in progress"
            : "No requests found"
        }
        emptyDescription={
          (dataset as any)?.meta?.status ===
            DatasetS3ImportStatus.ImportPending ||
          (dataset as any)?.meta?.status === DatasetS3ImportStatus.Importing
            ? "Requests will appear after the import completes. Progress is shown above."
            : "Add requests to this dataset to get started."
        }
      />
    </div>
  );

  const renderReviewsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Reviews</h3>
        </div>
        <Tooltip
          content={
            hasActiveReview
              ? `Review in progress${
                  activeReviewStatus ? ` (${activeReviewStatus})` : ""
                }. Check the table or refresh.`
              : "Start a new review"
          }>
          <div className="inline-block">
            <Button
              onClick={() => {
                if (hasActiveReview) {
                  console.log("Review already in progress");
                } else {
                  setIsAiReviewModalVisible(true);
                }
              }}
              disabled={hasActiveReview}
              className="gap-2">
              {isAiReviewLoading && (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              )}
              <BinocularsIcon className="w-4 h-4" />
              Review
            </Button>
          </div>
        </Tooltip>
      </div>

      <ReviewsTable
        reviews={reviews}
        loading={isReviewsLoading}
        currentPage={reviewsCurrentPage}
        totalPages={Math.ceil(totalReviews / reviewsPageSize)}
        totalItems={totalReviews}
        itemsPerPage={reviewsPageSize}
        onPageChange={setReviewsCurrentPage}
        onRetryReview={handleRetryReview}
        onForceProlificSync={handleForceProlificSync}
        syncingReviewIds={syncingReviewIds}
        reviewProgress={reviewProgress}
        onReviewClick={(review) => navigate(`/forge/datasets/${id}/reviews/${review.id}`)}
      />
    </div>
  );

  const renderAugmentationsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Augmentations</h3>
          <p className="text-sm text-muted-foreground">
            Generate additional training data
          </p>
        </div>
        <Button
          onClick={() => navigate(`/forge/datasets/${id}/edit`)}
          className="gap-2">
          <FlaskConicalIcon className="w-4 h-4" />
          New Augmentation
        </Button>
      </div>

      <AugmentationsTable
        augmentations={augmentations}
        loading={isAugmentationsLoading}
        currentPage={augmentationsCurrentPage}
        totalPages={Math.ceil(totalAugmentations / augmentationsPageSize)}
        totalItems={totalAugmentations}
        itemsPerPage={augmentationsPageSize}
        onPageChange={setAugmentationsCurrentPage}
        datasetId={id || ""}
      />
    </div>
  );

  const renderVersionsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground">Version History</h3>
        <p className="text-sm text-muted-foreground">
          All versions of this dataset, ordered by creation date
        </p>
      </div>

      <VersionsTable
        versions={versions}
        loading={versionsLoading}
        currentVersionId={id || ""}
      />
    </div>
  );

  // Tab configuration
  const tabDefinitions: TabDefinition[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <AppWindowIcon className="w-4 h-4" />,
      content: renderOverviewTab(),
    },
    {
      key: "requests",
      label: "Requests",
      icon: <FileSearchIcon className="w-4 h-4" />,
      content: renderRequestsTab(),
    },
    {
      key: "reviews",
      label: "Reviews",
      icon: <FileSearchIcon className="w-4 h-4" />,
      content: renderReviewsTab(),
    },
    {
      key: "augmentations",
      label: "Augmentations",
      icon: <FlaskConicalIcon className="w-4 h-4" />,
      content: renderAugmentationsTab(),
    },
    {
      key: "versions",
      label: "Versions",
      icon: <GitBranchIcon className="w-4 h-4" />,
      content: renderVersionsTab(),
    },
  ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map((tab) => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon,
  }));

  const importMeta: any = dataset?.meta || {};
  const rawImportStatus: string | undefined = importMeta.status;
  const importStatus: DatasetS3ImportStatus | undefined =
    isDatasetS3ImportStatus(rawImportStatus) ? rawImportStatus : undefined;
  const showImportBanner = importStatus !== undefined;
  const isUsedInTest = (dataset as any)?.meta?.used_in_test === true;
  const containsTestRequests =
    (dataset as any)?.meta?.contains_test_requests === true;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          <Card className="border-border">
            <CardContent className="p-8 space-y-4">
              <h1 className="text-2xl font-gc-demi-bold text-foreground">
                Failed to load dataset
              </h1>
              <p className="text-sm text-muted-foreground font-gc-reg">
                {loadError}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate("/forge/compositions?tab=datasets")}>
                  Back to Datasets
                </Button>
                <Button
                  onClick={() => {
                    setIsLoading(true);
                    setLoadError(null);
                    // Re-trigger the effect by reloading the page; avoids duplicating fetch logic.
                    window.location.reload();
                  }}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/forge/compositions?tab=datasets")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Datasets
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    {dataset?.name}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                    {dataset?.name}
                  </h1>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                    v{dataset?.major_version || 1}.{dataset?.minor_version || 0}
                  </span>
                  {isUsedInTest ? (
                    <Badge
                      variant="outline"
                      className="text-xs border-violet-500/30 text-violet-700 bg-violet-50 dark:bg-violet-950 dark:text-violet-300"
                    >
                      Contains Test Requests
                    </Badge>
                  ) : containsTestRequests ? (
                    <Badge
                      variant="outline"
                      className="text-xs border-violet-500/30 text-violet-700 bg-violet-50 dark:bg-violet-950 dark:text-violet-300"
                    >
                      Contains Test Requests
                    </Badge>
                  ) : null}
                </div>
                {/* Dataset Tags */}
                <div className="flex items-center gap-2 mt-2">
                  <TagIcon className="w-4 h-4 text-muted-foreground" />
                  <DatasetTags datasetId={Number(id)} />
                </div>
                <p className="text-lg text-muted-foreground font-gc-reg mt-2">
                  Manage dataset requests, run fine-tuning, and review model
                  improvements
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate(`/forge/datasets/${id}/edit`)}
                  className="gap-2">
                  <EditIcon className="w-4 h-4" />
                  Edit Dataset
                </Button>
                {(() => {
                  const meta: any = dataset?.meta || {};
                  const s3Url =
                    meta.s3_url || meta.import_s3_url || meta.s3_path;
                  const isS3 =
                    meta.datasource_type === "s3" ||
                    meta.data_source_type === "s3" ||
                    meta.import_source === "s3";
                  const status = String(meta.status || "");
                  const canReimport =
                    isS3 &&
                    s3Url &&
                    status !== "IMPORT_PENDING" &&
                    status !== "IMPORTING";
                  if (!canReimport) return null;
                  return (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!id) return;
                        setIsReimporting(true);
                        DatasetService.reimportS3(
                          Number(id),
                          undefined,
                          apiKey
                        ).subscribe(
                          () => {
                            DatasetService.getDataset(
                              Number(id),
                              undefined,
                              apiKey
                            ).subscribe(
                              (data: FinetuneDataset) => {
                                if (data) setDataset(data);
                                setIsReimporting(false);
                              },
                              () => setIsReimporting(false)
                            );
                          },
                          (error: any) => {
                            console.error("Error starting re-import:", error);
                            setIsReimporting(false);
                          }
                        );
                      }}
                      disabled={isReimporting}
                      className="gap-2">
                      {isReimporting ? (
                        <Loader2Icon className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCwIcon className="w-4 h-4" />
                      )}
                      Re-import
                    </Button>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>

        {showImportBanner && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {importStatus === DatasetS3ImportStatus.Importing && (
                      <Loader2Icon className="w-4 h-4 animate-spin text-maitai-green" />
                    )}
                    <span className="text-sm font-gc-med text-foreground">
                      {importStatus === DatasetS3ImportStatus.ImportPending
                        ? "S3 import queued"
                        : importStatus === DatasetS3ImportStatus.Importing
                        ? "Importing data from S3"
                        : importStatus === DatasetS3ImportStatus.Cancelled
                        ? "S3 import cancelled"
                        : "S3 import failed"}
                    </span>
                    {importStatus && (
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs shrink-0",
                          getS3ImportStatusBadgeClassName(importStatus)
                        )}>
                        {S3_IMPORT_STATUS_LABEL[importStatus]}
                      </Badge>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1">
                    {importStatus !== DatasetS3ImportStatus.Error && (
                      <>
                        {typeof importMeta.import_total_files === "number" && (
                          <div>
                            Files:{" "}
                            {typeof importMeta.import_files_copied === "number"
                              ? importMeta.import_files_copied
                              : importMeta.import_processed_files ?? 0}
                            /{importMeta.import_total_files}
                          </div>
                        )}
                        {typeof importMeta.import_total_requests ===
                          "number" && (
                          <div>
                            Requests ingested:{" "}
                            {importMeta.import_total_requests.toLocaleString()}
                          </div>
                        )}
                        {typeof importMeta.import_total_requests_added_to_dataset ===
                          "number" && (
                          <div>
                            Requests added to dataset:{" "}
                            {importMeta.import_total_requests_added_to_dataset.toLocaleString()}
                          </div>
                        )}
                      </>
                    )}

                    {importStatus === DatasetS3ImportStatus.Error && (
                      <div className="text-red-700 dark:text-red-300">
                        S3 import failed
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {(importStatus === DatasetS3ImportStatus.ImportPending ||
                    importStatus === DatasetS3ImportStatus.Importing) && (
                    <AlertDialog
                      open={isCancelDialogOpen}
                      onOpenChange={setIsCancelDialogOpen}>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isCancellingImport}
                          className="hover:border-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950">
                          Cancel
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel S3 import?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will stop the background import job. Requests
                            that have already been ingested won’t be rolled
                            back.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={isCancellingImport}>
                            Keep importing
                          </AlertDialogCancel>
                          <AlertDialogAction
                            disabled={isCancellingImport}
                            className="bg-red-600 text-white hover:bg-red-700"
                            onClick={() => {
                              if (!id) return;
                              setIsCancellingImport(true);
                              DatasetService.cancelS3Import(
                                Number(id),
                                undefined,
                                apiKey
                              ).subscribe(
                                () => {
                                  DatasetService.getDataset(
                                    Number(id),
                                    undefined,
                                    apiKey
                                  ).subscribe(
                                    (data: FinetuneDataset) => {
                                      if (data) setDataset(data);
                                      setIsCancellingImport(false);
                                      setIsCancelDialogOpen(false);
                                    },
                                    (error: any) => {
                                      console.error(
                                        "Error refreshing dataset after cancel:",
                                        error
                                      );
                                      setIsCancellingImport(false);
                                      setIsCancelDialogOpen(false);
                                    }
                                  );
                                },
                                (error: any) => {
                                  console.error(
                                    "Error cancelling S3 import:",
                                    error
                                  );
                                  setIsCancellingImport(false);
                                  setIsCancelDialogOpen(false);
                                }
                              );
                            }}>
                            Cancel import
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <OverflowTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className="border-b border-border px-8 py-4"
            />

            {/* Tab Content */}
            <div className="p-8">
              {tabDefinitions.find((tab) => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dataset Modification Modal */}
      {dataset && (
        <DatasetModification
          datasetId={Number(id)}
          isVisible={isModificationModalVisible}
          onClose={() => setIsModificationModalVisible(false)}
        />
      )}

      {/* Request View Modal */}
      {selectedRequest && (
        <RequestViewModal
          request={selectedRequest}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          apiKey={apiKey}
        />
      )}

      {/* AI Review Confirmation Modal */}
      <AIReview
        isVisible={isAiReviewModalVisible}
        onClose={() => setIsAiReviewModalVisible(false)}
        onStartReview={handleStartAiReview}
        loading={isAiReviewLoading}
        datasetId={id ? Number(id) : undefined}
        apiKey={apiKey}
        humanReviewInProgress={hasHumanReviewInProgress}
        humanReviewStatus={humanReviewStatus}
      />
    </div>
  );
};

export default DatasetDetail;
